import { computed } from "vue";
import { defaultSortConfig } from "@/config/columnSortConfig";

const createSortingUtils = (customConfig = {}) => {
  const config = { ...defaultSortConfig, ...customConfig };

  const extractValue = (value) => {
    if (value == null || typeof value !== "object") return value;
    for (const extractor of config.valueExtractors) {
      const extracted = extractor(value);
      if (extracted != null) return extracted;
    }
    return value;
  };

  const parseForSorting = (value) => {
    const extracted = extractValue(value);
    if (extracted == null) {
      return { type: "null", value: null, compare: null };
    }
    for (const detector of config.typeDetectors) {
      if (detector.detect(extracted)) {
        return {
          type: detector.name,
          value: detector.parse(extracted),
          compare: detector.compare,
        };
      }
    }
    throw new Error(`No type detector matched for value: ${extracted}`);
  };

  const compareValues = (a, b, direction = "asc") => {
    const aParsed = parseForSorting(a);
    const bParsed = parseForSorting(b);

    // handle nulls
    if (aParsed.type === "null" && bParsed.type === "null") {
      return config.nullHandling.bothNull();
    }
    if (aParsed.type === "null" || bParsed.type === "null") {
      const isFirstNull = aParsed.type === "null";
      const nullComp = config.nullHandling.oneNull(isFirstNull);
      return direction === "asc" ? nullComp : -nullComp;
    }

    // fallback to string
    if (aParsed.type !== bParsed.type) {
      const aStr = String(aParsed.value ?? "").toLowerCase();
      const bStr = String(bParsed.value ?? "").toLowerCase();
      const result = aStr.localeCompare(bStr);
      return direction === "asc" ? result : -result;
    }

    // type-specific compare
    const result = aParsed.compare(aParsed.value, bParsed.value);
    return direction === "asc" ? result : -result;
  };

  return { extractValue, parseForSorting, compareValues };
};

export function useTable(props, sortConfig, customSortConfig = {}) {
  const sortingUtils = createSortingUtils(customSortConfig);

  const activeFilters = computed(() => {
    return props.filters.reduce((acc, filter) => {
      if (!filter.column) return acc;
      acc[filter.column] = filter.items
        .filter((item) => item.value)
        .map((item) => item.name);
      return acc;
    }, {});
  });

  const filteredRows = computed(() => {
    let rows = props.rows;
    for (const [columnName, allowedValues] of Object.entries(
      activeFilters.value
    )) {
      if (allowedValues.length === 0) {
        return []; // no options selected in this filter = no rows should match
      }
      rows = rows.filter((row) => allowedValues.includes(row[columnName]));
    }

    if (props.searchQuery) {
      const search = props.searchQuery.toLowerCase();
      rows = rows.filter((row) =>
        (row[props.searchOnColumn] ?? "").toLowerCase().includes(search)
      );
    }
    return rows;
  });

  const sortedAndFilteredRows = computed(() => {
    if (!sortConfig.value.column || !sortConfig.value.direction) {
      return filteredRows.value;
    }
    return [...filteredRows.value].sort((a, b) =>
      sortingUtils.compareValues(
        a[sortConfig.value.column],
        b[sortConfig.value.column],
        sortConfig.value.direction
      )
    );
  });

  const visibleColumns = computed(() => {
    if (props.disableColumnDropdown) {
      const ignored = props.ignoredKeys || new Set();

      if (!Array.isArray(props.rows) || props.rows.length === 0) return [];

      return Object.keys(props.rows[0]).filter((key) => !ignored.has(key));
    }

    return props.columnVisibility
      .filter((col) => col.value)
      .map((col) => col.name);
  });

  return {
    filteredRows,
    sortedAndFilteredRows,
    visibleColumns,
  };
}
